import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Event interface representing an event in the system
 */
interface Event {
  id: string;
  name: string;
  description: string;
  location: string;
  date: string;
  endDate?: string;
  image?: string;
  organizerId: string;
  organizerName: string;
  attendeeCount: number;
  isCheckedIn: boolean;
  tags: string[];
  category: string;
  website?: string;
  maxAttendees?: number;
  price?: number;
  currency?: string;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
}

/**
 * Question interface representing a question/post in the system
 */
interface Question {
  id: string;
  authorId: string;
  authorName: string;
  authorAvatar?: string;
  eventId?: string;
  eventName?: string;
  title: string;
  description: string;
  image?: string;
  tags: string[];
  createdAt: string;
  visibility: 'anyone' | 'network' | 'event';
  isAnonymous: boolean;
  isUpvoted: boolean;
  isMeToo: boolean;
  isBookmarked: boolean;
  replies: number;
}

/**
 * Message interface for chat system
 */
export interface Message {
  id: string;
  senderId: string;
  receiverId: string;
  questionId?: string;
  content: string;
  createdAt: string;
  isRead: boolean;
  type: 'text' | 'image' | 'file' | 'preview';
  isPublicVisible?: boolean; // Whether others can see this user offered help
}

// AIDEV-NOTE: Chat state moved to React Query - only keeping help-related functionality for questions

/**
 * App state interface
 */
interface AppState {
  events: Event[];
  questions: Question[];
  // Badge state lives in the React Query cache (`['chats','unread-count']`,
  // `['notification-count']`) — no Zustand mirror.
  activeTab: 'meetverse' | 'my-spaces';
  hasExplicitlyChosenTab: boolean;
  sortByTab: Record<
    'meetverse' | 'my-spaces',
    'latest' | 'most_uplifted' | 'most_helpers' | 'most_relatable' | 'bookmarked'
  >;
}

/**
 * App actions interface
 */
interface AppActions {
  // Events
  setEvents: (events: Event[]) => void;

  // Questions
  setQuestions: (questions: Question[]) => void;
  updateQuestion: (questionId: string, updates: Partial<Question>) => void;
  toggleBookmark: (questionId: string) => void;

  // Tab and sorting
  setActiveTab: (tab: AppState['activeTab']) => void;
  setSortByForTab: (tab: AppState['activeTab'], sortBy: AppState['sortByTab']['meetverse']) => void;
}

/**
 * Main app store using Zustand
 */
// AIDEV-NOTE: Added persist middleware to retain activeTab and sortByTab across refreshes. Migrated v1→v2.
export const useAppStore = create<AppState & AppActions>()(
  persist(
    (set, _get) => ({
      // Initial state
      events: [],
      questions: [],
      activeTab: 'meetverse',
      hasExplicitlyChosenTab: false,
      sortByTab: { meetverse: 'latest', 'my-spaces': 'latest' },

      // Event actions
      setEvents: events => {
        set({ events });
      },

      // Question actions
      setQuestions: questions => {
        set({ questions });
      },

      updateQuestion: (questionId, updates) => {
        set(state => ({
          questions: state.questions.map(question =>
            question.id === questionId ? { ...question, ...updates } : question,
          ),
        }));
      },

      toggleBookmark: _questionId => {
        // COMMENTED OUT: Local bookmark toggle for future reference
        /*
        set((state) => ({
          questions: state.questions.map((question) =>
            question.id === questionId
              ? { ...question, isBookmarked: !question.isBookmarked }
              : question
          ),
        }));
        */
        // AIDEV-TODO: Replace with API call to create/delete interaction
      },

      // Tab and sort actions
      setActiveTab: tab => {
        set({ activeTab: tab, hasExplicitlyChosenTab: true });
      },
      setSortByForTab: (tab, sortBy) => {
        set(state => ({
          sortByTab: { ...state.sortByTab, [tab]: sortBy },
        }));
      },
    }),
    {
      name: 'meetball-app-store',
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        activeTab: state.activeTab,
        hasExplicitlyChosenTab: state.hasExplicitlyChosenTab,
        sortByTab: state.sortByTab,
      }),
      version: 3,
      migrate: (persistedState: unknown, version: number) => {
        if (version < 2) {
          const old = persistedState as { activeFilters?: string[]; sortBy?: string };
          return {
            activeTab: 'meetverse' as const,
            hasExplicitlyChosenTab: false,
            sortByTab: {
              meetverse: (old?.sortBy || 'latest') as
                'latest' | 'most_uplifted' | 'most_helpers' | 'most_relatable' | 'bookmarked',
              'my-spaces': 'latest' as const,
            },
          };
        }
        if (version < 3) {
          return { ...(persistedState as object), hasExplicitlyChosenTab: false };
        }
        return persistedState;
      },
    },
  ),
);
